import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { ROUTES } from 'shared/constants/routes';
import { TAGS_SPECIALTY, colorPalette } from 'shared/constants';
import { getLang } from 'shared/store/selectors/lang.selector';
import { getOwnersList } from 'shared/store/selectors/owners.selector';
import { isAccountAdmin } from 'shared/store/selectors/auth.selector';
import { getUser } from 'shared/store/selectors/user.selector';
import { formatFullName, sortByAlphabet } from 'shared/utils';
import { ItemType } from 'src/components/core/knowledge';
import { Owners } from 'src/components/common/owners';
import { LearnMore } from 'src/components/common/learn-more';
import { ExtendExpirationButtons } from 'src/components/knowledge-content/knowledge-content-editor';
import {
  SetFilter,
  RadioSetFilter,
  cellActionKeyboardNavigate,
  serverSideFilterDoesFilterPass,
} from 'src/components/design-system/table';
import { ExpiryDate, ViewsModalTriggerButton } from '../components';
import { EXPIRY_STATUS } from '../constants';
import { isSyncedContentItem } from '../my-content.utils';
import styles from '../my-content.module.scss';

/**
 * Hook for managing My Content table columns
 * @param {Object} params
 * @param {Object} params.lang - Language strings
 * @param {Object} params.filters - Current filter state
 * @param {string} params.sortBy - Current sort column ID
 * @param {string} params.sortOrder - Current sort order ('asc' or 'desc')
 * @param {Array} params.categoriesWithItems - Categories for path filter options
 * @param {Function} params.handleItemClick - Handler for item click
 * @param {Function} params.handleOwnerClick - Handler for owner click
 * @param {Function} params.handlePathClick - Handler for path click
 * @param {Function} params.handleItemExpirationExtension - Handler for expiration extension
 * @param {Function} params.setViewsModalItem - Setter for views modal item
 */
export const useColumns = ({
  lang,
  filters,
  sortBy,
  sortOrder,
  categoriesWithItems,
  handleItemClick,
  handleOwnerClick,
  handlePathClick,
  handleItemExpirationExtension,
  setViewsModalItem,
}) => {
  const LANG_TAGS = useSelector(getLang('TAGS'));
  const actionTooltipLang = useSelector(getLang('ACTION_BUTTON_TOOLTIPS'));
  const categoriesById = useSelector(({ knowledge }) => knowledge.categoriesById);
  const ownersListForEditor = useSelector(({ owners }) => getOwnersList(owners));
  const userIsAccountAdmin = useSelector(isAccountAdmin);
  const user = useSelector(getUser);

  // Create filter options for columns (sorted alphabetically)
  const pathFilterOptions = useMemo(
    () =>
      categoriesWithItems
        .filter(cat => cat.title && cat.id)
        .map(cat => ({ text: cat.title, value: cat.id }))
        .sort((a, b) => sortByAlphabet(a.text, b.text)),
    [categoriesWithItems],
  );

  const ownerFilterOptions = useMemo(
    () =>
      ownersListForEditor
        .map(owner => ({ text: formatFullName(owner), value: owner.id }))
        .sort((a, b) => sortByAlphabet(a.text, b.text)),
    [ownersListForEditor],
  );

  const specialtyFilterOptions = useMemo(
    () =>
      Object.values(TAGS_SPECIALTY)
        .map(specialty => ({ text: LANG_TAGS[specialty] || specialty, value: specialty }))
        .sort((a, b) => sortByAlphabet(a.text, b.text)),
    [LANG_TAGS],
  );

  const expiryFilterOptions = useMemo(
    () => [
      { text: lang.EXPIRED, value: EXPIRY_STATUS.EXPIRED },
      { text: lang.ABOUT_TO_EXPIRE, value: EXPIRY_STATUS.ABOUT_TO_EXPIRE },
      { text: lang.EXPIRES_IN_3_MONTHS, value: EXPIRY_STATUS.EXPIRES_IN_3_MONTHS },
    ],
    [lang],
  );

  const sharedFilterOptions = useMemo(
    () => [
      { text: lang.FILTER_YES || 'Yes', value: true },
      { text: lang.FILTER_NO || 'No', value: false },
    ],
    [lang],
  );

  // Track whether initial sort has been applied - after first render, AG Grid owns sort state
  const initialSortAppliedRef = useRef(false);

  // Mark initial sort as applied after first render
  useEffect(() => {
    initialSortAppliedRef.current = true;
  }, []);

  // Helper to get sort prop - only set on initial render, then let AG Grid manage it
  const getSortForColumn = useCallback(
    colId => {
      if (initialSortAppliedRef.current) return undefined; // Don't touch AG Grid's internal state
      return sortBy === colId ? sortOrder : null;
    },
    [sortBy, sortOrder],
  );

  // No-op comparator - disables client-side sorting while keeping sort indicators
  // Server handles actual sorting, this prevents AG Grid from reordering rows locally
  const serverSideComparator = () => 0;

  // AG Grid column definitions - sort is only set on initial render, then AG Grid owns it
  const columns = useMemo(
    /**
     * @type {() => import('ag-grid-community').ColDef[]}
     */
    () => [
      {
        headerName: lang.COLUMN_ITEM,
        field: 'title',
        colId: 'title',
        sortable: true,
        sort: getSortForColumn('title'),
        comparator: serverSideComparator,
        cellRenderer: params => {
          if (!params.data) return null;
          return params.value ? (
            <div className={styles.itemTitleContainer}>
              <ItemType type={params.data.type} className={styles.itemIcon} />
              <Link
                className={styles.link}
                to={ROUTES.KNOWLEDGE_ITEMS.replace(':id', params.data.id)}
                onClick={() => handleItemClick(params.data)}>
                {params.value}
              </Link>
            </div>
          ) : (
            '-'
          );
        },
        suppressKeyboardEvent: cellActionKeyboardNavigate,
        flex: 3,
        minWidth: 250,
      },
      {
        headerName: lang.COLUMN_PATH || 'Path',
        field: 'links',
        colId: 'path',
        sortable: true,
        sort: getSortForColumn('path'),
        comparator: serverSideComparator,
        cellRenderer: params => {
          if (!params.data) return null;
          const links = params.value;
          if (!links || !Array.isArray(links)) return '-';

          const validLinks = links
            .filter(link => link.title && link.categoryId)
            .sort((a, b) => a.title.localeCompare(b.title));

          if (validLinks.length === 0) return '-';

          return (
            <span className={styles.cellText}>
              {validLinks.map((link, index) => {
                const binderUrl = `${ROUTES.KNOWLEDGE}?category=${link.categoryId}`;
                return (
                  <React.Fragment key={link.categoryId}>
                    <Link
                      className={styles.link}
                      to={binderUrl}
                      onClick={() => handlePathClick(link.categoryId)}>
                      {link.title}
                    </Link>
                    {index < validLinks.length - 1 && ', '}
                  </React.Fragment>
                );
              })}
            </span>
          );
        },
        suppressKeyboardEvent: cellActionKeyboardNavigate,
        filter: {
          component: SetFilter,
          doesFilterPass: serverSideFilterDoesFilterPass,
        },
        filterParams: {
          options: pathFilterOptions,
          applyMode: true,
        },
        flex: 1.5,
        minWidth: 140,
      },
      {
        headerName: lang.COLUMN_OWNERS,
        field: 'ownerIds',
        colId: 'owners',
        sortable: true,
        sort: getSortForColumn('owners'),
        comparator: serverSideComparator,
        cellRenderer: params => {
          if (!params.data) return null;
          return (
            <div style={{ wordBreak: 'break-all', overflowWrap: 'break-word' }}>
              <Owners
                ownerIds={params.value}
                onNavigate={ownerId => {
                  handleOwnerClick(ownerId);
                }}
              />
            </div>
          );
        },
        suppressKeyboardEvent: cellActionKeyboardNavigate,
        filter: userIsAccountAdmin
          ? {
              component: SetFilter,
              doesFilterPass: serverSideFilterDoesFilterPass,
            }
          : undefined,
        filterParams: userIsAccountAdmin
          ? {
              options: ownerFilterOptions,
              applyMode: true,
            }
          : undefined,
        flex: 1.4,
        minWidth: 150,
      },
      {
        headerName: lang.COLUMN_SPECIALTY,
        field: 'tags',
        colId: 'specialty',
        sortable: true,
        sort: getSortForColumn('specialty'),
        comparator: serverSideComparator,
        cellRenderer: params => {
          if (!params.data) return null;
          const tags = params.value;
          if (!tags) return '-';
          const specialty = tags.specialty ? LANG_TAGS[tags.specialty] || tags.specialty : null;
          const subspecialty = tags.subspecialty
            ? LANG_TAGS[tags.subspecialty] || tags.subspecialty
            : null;

          let text = '-';
          if (specialty && subspecialty) {
            text = `${specialty}, ${subspecialty}`;
          } else if (specialty) {
            text = specialty;
          } else if (subspecialty) {
            text = subspecialty;
          }
          return <span className={styles.cellText}>{text}</span>;
        },
        filter: {
          component: SetFilter,
          doesFilterPass: serverSideFilterDoesFilterPass,
        },
        filterParams: {
          options: specialtyFilterOptions,
          applyMode: true,
        },
        flex: 1.5,
        minWidth: 180,
      },
      {
        headerName: lang.COLUMN_EXPIRES_ON,
        field: 'expiresAt',
        colId: 'expiresAt',
        sortable: true,
        sort: getSortForColumn('expiresAt'),
        comparator: serverSideComparator,
        cellRenderer: params => {
          if (!params.data) return null;
          return <ExpiryDate date={params.value} />;
        },
        filter: {
          component: RadioSetFilter,
          doesFilterPass: serverSideFilterDoesFilterPass,
        },
        filterParams: {
          options: expiryFilterOptions.map(opt => ({
            value: opt.value,
            label: opt.text,
          })),
        },
        flex: 1.5,
        minWidth: 150,
      },
      {
        headerName: lang.COLUMN_EXTEND_BY,
        field: 'extendBy',
        colId: 'extendBy',
        sortable: false,
        cellRenderer: params => {
          if (!params.data) return null;
          // Synced: the provider owns the expiration date — the server answers 403.
          const isSynced = isSyncedContentItem(params.data, categoriesById);
          // No stored date = never expires (the type-driven model is parked with BB-29);
          // a new date is set through the editor, not nudged with "extend by".
          const neverExpires = !params.data.expiresAt;
          return (
            <ExtendExpirationButtons
              expiresAt={params.data.expiresAt}
              showExpirationMessage={false}
              useShortLabels
              usePrimaryButtons
              showExtendByText={false}
              buttonStyle={styles.extendButton}
              disabled={isSynced || neverExpires}
              disabledTooltip={
                isSynced
                  ? actionTooltipLang.READ_ONLY_SYNCED
                  : actionTooltipLang.NO_REVIEW_DATE_TO_EXTEND
              }
              onExtendExpiration={newDate => handleItemExpirationExtension(params.data.id, newDate)}
            />
          );
        },
        suppressKeyboardEvent: cellActionKeyboardNavigate,
        cellClass: styles.centeredCell,
        flex: 1.0,
        minWidth: 120,
      },
      {
        headerName: lang.COLUMN_VIEWS_90D,
        field: 'views90d',
        colId: 'views90d',
        sortable: true,
        sort: getSortForColumn('views90d'),
        comparator: serverSideComparator,
        cellRenderer: params => {
          if (!params.data) return null;
          return (
            <ViewsModalTriggerButton
              record={params.data}
              period="last90Days"
              onViewsClick={setViewsModalItem}
            />
          );
        },
        suppressKeyboardEvent: cellActionKeyboardNavigate,
        flex: 1.5,
        minWidth: 130,
        cellClass: styles.centeredCell,
      },
      {
        headerName: lang.COLUMN_VIEWS_ALL_TIME,
        field: 'viewsAllTime',
        colId: 'viewsAllTime',
        sortable: true,
        cellClass: styles.centeredCell,
        sort: getSortForColumn('viewsAllTime'),
        comparator: serverSideComparator,
        cellRenderer: params => {
          if (!params.data) return null;
          return (
            <ViewsModalTriggerButton
              record={params.data}
              period="total"
              onViewsClick={setViewsModalItem}
            />
          );
        },
        suppressKeyboardEvent: cellActionKeyboardNavigate,
        flex: 1.5,
        minWidth: 130,
      },
      {
        headerName: lang.COLUMN_SHARED_ON_KNOWLEDGE_NETWORK,
        field: 'shared',
        colId: 'shared',
        sortable: true,
        cellClass: styles.centeredCell,
        sort: getSortForColumn('shared'),
        comparator: serverSideComparator,
        cellRenderer: params => {
          if (!params.data) return null;
          return (
            <>
              <span className="sr-only">
                {params.value?.isShared ? lang.SHARED_TO_HUB : lang.NOT_SHARED_TO_HUB}
              </span>
              <FontAwesomeIcon
                icon={params.value?.isShared ? faCheck : faTimes}
                color={params.value?.isShared ? colorPalette.greenPrimary : colorPalette.red}
              />
            </>
          );
        },
        filter: {
          component: RadioSetFilter,
          doesFilterPass: serverSideFilterDoesFilterPass,
        },
        filterParams: {
          options: sharedFilterOptions.map(opt => ({
            value: opt.value,
            label: opt.text,
          })),
        },
        flex: 1.5,
        minWidth: 160,
      },
    ],
    [
      lang.COLUMN_ITEM,
      lang.COLUMN_PATH,
      lang.COLUMN_OWNERS,
      lang.COLUMN_SPECIALTY,
      lang.COLUMN_EXPIRES_ON,
      lang.COLUMN_EXTEND_BY,
      lang.COLUMN_VIEWS_90D,
      lang.COLUMN_VIEWS_ALL_TIME,
      lang.COLUMN_SHARED_ON_KNOWLEDGE_NETWORK,
      lang.SHARED_TO_HUB,
      lang.NOT_SHARED_TO_HUB,
      getSortForColumn,
      pathFilterOptions,
      userIsAccountAdmin,
      ownerFilterOptions,
      specialtyFilterOptions,
      expiryFilterOptions,
      sharedFilterOptions,
      handleItemClick,
      handlePathClick,
      handleOwnerClick,
      LANG_TAGS,
      actionTooltipLang.READ_ONLY_SYNCED,
      actionTooltipLang.NO_REVIEW_DATE_TO_EXTEND,
      categoriesById,
      handleItemExpirationExtension,
      setViewsModalItem,
    ],
  );

  // Static filters that cannot be removed by the user (e.g., owner filter for non-admin users)
  const staticFilters = useMemo(() => {
    if (userIsAccountAdmin) return {};

    const userId = user?.id;
    if (!userId) return {};

    const ownerOption = ownerFilterOptions.find(opt => opt.value === userId);
    const displayName = ownerOption?.text || formatFullName(user);

    return {
      owners: [
        {
          value: userId,
          label: displayName,
          extra: (
            <LearnMore
              text={lang.COLLABORATOR_FILTER_TOOLTIP}
              link="https://portal.help.c8health.com/servicedesk/customer/kb/view/1418362971"
              size="md"
            />
          ),
        },
      ],
    };
  }, [userIsAccountAdmin, user, ownerFilterOptions, lang.COLLABORATOR_FILTER_TOOLTIP]);

  // Map colId to filteredValue for ActiveFilters
  const filterValues = useMemo(
    () => ({
      path: filters.path || null,
      owners: filters.owners || null,
      specialty: filters.specialty || null,
      expiresAt: filters.expiresAt ? [filters.expiresAt] : null,
      shared: filters.shared != null ? [filters.shared] : null, // != null checks for both null and undefined
    }),
    [filters.path, filters.owners, filters.specialty, filters.expiresAt, filters.shared],
  );

  // Derive activeFiltersColumns from columns
  const activeFiltersColumns = useMemo(
    () =>
      columns
        .filter(col => col.filter)
        .map(col => ({
          ...col,
          filteredValue: filterValues[col.colId] || null,
        })),
    [columns, filterValues],
  );

  return {
    columns,
    activeFiltersColumns,
    filterValues,
    staticFilters,
  };
};
